import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
    PiArrowCounterClockwiseBold,
    PiDotsSixVerticalBold,
    PiEqualsBold,
    PiCloudArrowDownBold,
    PiLockSimpleBold,
    PiPlusBold,
    PiTrashBold,
    PiWarningBold,
} from 'react-icons/pi';
import { Button, Skeleton } from '@v2/design-system';
import V2Modal from '@v2/components/ui/V2Modal';
import MoneyInput from '@v2/components/ui/MoneyInput';
import api from '@v2/lib/api';
import { cn } from '@v2/utils/cn';
import { useI18n } from '@v2/providers/I18nProvider';
import { formatBrl } from '../utils';
import { invalidateDealCommissionQueries } from './invalidateDealCommissionQueries';

type PreviewRow = {
    id: number | null;
    key: string;
    label: string;
    is_entry: boolean;
    due_date: string | null;
    new_due_date: string | null;
    amount: number | null;
    new_amount: number | null;
    status: string;
    locked: boolean;
    action: 'keep' | 'change' | 'add' | 'drop';
    commission: number | null;
    new_commission: number | null;
};

type Preview = {
    deal: { id: number; sale_number: string; amount: number; locked: boolean };
    rows: PreviewRow[];
    summary: { installments_now: number; installments_sum_now: number; installments_after: number; installments_sum_after: number; tolerance: number };
    sellers: Array<{ user_id: number; name: string; target: number; rate_pp: number; current: number; paid: number }>;
    reattached: Array<{ record_id: number; amount: number; label: string }>;
    warnings: string[];
    errors: string[];
};

type Kind = 'entry' | 'installment';

type PloomesMirror = {
    quote_id: number | null;
    quote_number: string | number | null;
    origin_quote_id: number | null;
    origin_matches: boolean | null;
    quote_total: number;
    sum: number;
    deal_amount: number;
    entries: Array<{ due_date: string; amount: number }>;
    installments: Array<{ due_date: string; amount: number }>;
};

/** Linha editável: parcela existente (id) ou nova (id null). */
type Line = {
    uid: string;
    id: number | null;
    kind: Kind;
    due_date: string;
    amount: number | null;
    locked: boolean;
    dropped: boolean;
    commission: number | null;
    original: { due_date: string; amount: number } | null;
};

const inputClass =
    'w-full rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 text-sm tabular-nums outline-none focus:border-violet-400 focus:ring-2 focus:ring-violet-100 disabled:cursor-not-allowed disabled:bg-gray-50 disabled:text-gray-400 dark:border-zinc-700 dark:bg-zinc-900 dark:focus:ring-violet-900/40 dark:disabled:bg-zinc-800';

function apiMessage(err: unknown): string | null {
    const data = (err as { response?: { data?: { message?: string; errors?: Record<string, string[]> } } })?.response?.data;
    return Object.values(data?.errors ?? {})[0]?.[0] ?? data?.message ?? null;
}

// Taxa em %: aceita vírgula ou ponto como decimal ("1,5" e "1.5" = 1,5%).
const parseRate = (value: string) => Number(value.trim().replace(',', '.'));
const validDate = (value: string) => /^\d{4}-\d{2}-\d{2}$/.test(value);

/** Vencimento crescente; linha nova sem data fica no fim da seção. */
function sortByDue(lines: Line[]): Line[] {
    return [...lines].sort((a, b) => {
        const da = validDate(a.due_date) ? a.due_date : '9999-12-31';
        const db = validDate(b.due_date) ? b.due_date : '9999-12-31';
        return da.localeCompare(db);
    });
}

export default function DealScheduleRepairModal({ dealId, open, onClose }: { dealId: number | string; open: boolean; onClose: () => void }) {
    const { t } = useI18n();
    const queryClient = useQueryClient();
    const [base, setBase] = useState<Preview | null>(null);
    const [preview, setPreview] = useState<Preview | null>(null);
    const [lines, setLines] = useState<Line[]>([]);
    const [rate, setRate] = useState('');
    const [reason, setReason] = useState('');
    const [stale, setStale] = useState(false);
    const [dragUid, setDragUid] = useState<string | null>(null);
    const [mirror, setMirror] = useState<PloomesMirror | null>(null);
    const url = `/commissioning/deals/${dealId}/actions/schedule-repair`;

    const newLines = lines.filter((line) => line.id == null);
    const newKey = (line: Line) => `new-${newLines.indexOf(line)}`;

    const payload = useMemo(() => {
        const changes: Record<number, { due_date?: string; amount?: number }> = {};
        for (const line of lines) {
            if (line.id == null || line.dropped || !line.original) continue;
            const change: { due_date?: string; amount?: number } = {};
            if (validDate(line.due_date) && line.due_date !== line.original.due_date) change.due_date = line.due_date;
            if (line.amount != null && Math.abs(line.amount - line.original.amount) > 0.004) change.amount = line.amount;
            if (Object.keys(change).length > 0) changes[line.id] = change;
        }

        return {
            drop: lines.filter((line) => line.id != null && line.dropped).map((line) => line.id as number),
            changes,
            add: lines.filter((line) => line.id == null).map((line) => ({ type: line.kind, due_date: line.due_date, amount: line.amount ?? 0 })),
            rate: rate.trim() === '' ? null : parseRate(rate),
        };
    }, [lines, rate]);

    const simulate = useMutation({
        mutationFn: (body: object) => api.post(`${url}/preview`, body).then((r) => r.data.data as Preview),
        onSuccess: (data) => {
            setPreview(data);
            setStale(false);
        },
        onError: (err) => toast.error(apiMessage(err) ?? t('v2.schedule_repair.failed')),
    });

    const apply = useMutation({
        mutationFn: () => api.post(url, { ...payload, reason }),
        onSuccess: async (res) => {
            toast.success(res.data?.message ?? t('v2.schedule_repair.done'));
            await invalidateDealCommissionQueries(queryClient, dealId);
            await queryClient.invalidateQueries({ queryKey: ['commissioning', 'deal-actions', dealId] });
            onClose();
        },
        onError: (err) => toast.error(apiMessage(err) ?? t('v2.schedule_repair.failed')),
    });

    const loadMirror = useMutation({
        mutationFn: () => api.get(`${url}/ploomes`).then((r) => r.data.data as PloomesMirror),
        onSuccess: (data) => setMirror(data),
        onError: (err) => toast.error(apiMessage(err) ?? t('v2.schedule_repair.ploomes_error')),
    });

    /**
     * Coloca o cronograma da proposta na tela, posição por posição em cada seção: parcela paga fica
     * como está, a que sobra no Hub é marcada para cancelar e a que falta é incluída.
     */
    const applyMirror = () => {
        if (!mirror) return;
        setLines((prev) => {
            const next: Line[] = [];
            (['entry', 'installment'] as const).forEach((kind) => {
                const source = kind === 'entry' ? mirror.entries : mirror.installments;
                const current = sortByDue(prev.filter((line) => line.kind === kind && line.id != null));
                const size = Math.max(source.length, current.length);
                for (let i = 0; i < size; i++) {
                    const line = current[i];
                    const row = source[i];
                    if (line && line.locked) {
                        next.push(line);
                    } else if (line && row) {
                        next.push({ ...line, due_date: row.due_date, amount: row.amount, dropped: false });
                    } else if (line) {
                        next.push({ ...line, dropped: true });
                    } else if (row) {
                        next.push({ uid: `new-${kind}-${i}-${Date.now()}`, id: null, kind, due_date: row.due_date, amount: row.amount, locked: false, dropped: false, commission: null, original: null });
                    }
                }
            });
            return next;
        });
        touch();
        toast.success(t('v2.schedule_repair.ploomes_applied'));
    };

    useEffect(() => {
        if (!open) return;
        setLines([]);
        setMirror(null);
        setRate('');
        setReason('');
        setPreview(null);
        setBase(null);
        api.post(`${url}/preview`, {})
            .then((r) => {
                const data = r.data.data as Preview;
                setBase(data);
                setPreview(data);
                setStale(false);
                setLines(
                    data.rows
                        .filter((row) => row.id != null && row.action !== 'drop')
                        .map((row) => ({
                            uid: `id-${row.id}`,
                            id: row.id,
                            kind: row.is_entry ? 'entry' : 'installment',
                            due_date: row.due_date ?? '',
                            amount: row.amount,
                            locked: row.locked,
                            dropped: false,
                            commission: row.commission,
                            original: { due_date: row.due_date ?? '', amount: row.amount ?? 0 },
                        })),
                );
            })
            .catch((err) => toast.error(apiMessage(err) ?? t('v2.schedule_repair.failed')));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, dealId]);

    const touch = () => setStale(true);
    const update = (uid: string, patch: Partial<Line>) => {
        setLines((prev) => prev.map((line) => (line.uid === uid ? { ...line, ...patch } : line)));
        touch();
    };

    const group = (kind: Kind) => sortByDue(lines.filter((line) => line.kind === kind));

    /**
     * Arrastar troca a ordem dentro da seção: os vencimentos ficam nas posições e o valor acompanha a
     * linha. Parcela paga ou cancelada não se move.
     */
    const reorder = (kind: Kind, fromUid: string, toUid: string) => {
        const ordered = group(kind);
        const movable = ordered.filter((line) => !line.locked && !line.dropped);
        const from = movable.findIndex((line) => line.uid === fromUid);
        const to = movable.findIndex((line) => line.uid === toUid);
        if (from < 0 || to < 0 || from === to) return;
        const slots = movable.map((line) => line.due_date);
        const next = [...movable];
        const [moved] = next.splice(from, 1);
        next.splice(to, 0, moved);
        const dates = new Map(next.map((line, index) => [line.uid, slots[index]]));
        setLines((prev) => prev.map((line) => (dates.has(line.uid) ? { ...line, due_date: dates.get(line.uid) as string } : line)));
        touch();
    };

    const resultByKey = useMemo(() => new Map((preview?.rows ?? []).map((row) => [row.key, row])), [preview]);
    const resultOf = (line: Line) => resultByKey.get(line.id != null ? String(line.id) : newKey(line));

    // Soma ao vivo: o total precisa fechar com o valor da venda (só centavos de arredondamento).
    const live = useMemo(() => {
        const active = lines.filter((line) => !line.dropped);
        const sum = active.reduce((acc, line) => acc + (line.amount ?? 0), 0);
        const diff = Math.round((sum - (base?.deal.amount ?? 0)) * 100) / 100;
        const lastEntry = active.filter((l) => l.kind === 'entry' && validDate(l.due_date)).map((l) => l.due_date).sort().pop();
        const firstRegular = active.filter((l) => l.kind === 'installment' && validDate(l.due_date)).map((l) => l.due_date).sort()[0];

        return {
            count: active.length,
            sum: Math.round(sum * 100) / 100,
            diff,
            ok: Math.abs(diff) <= Math.max(1, active.length) * 0.01 + 0.0001,
            orderOk: !lastEntry || !firstRegular || firstRegular.slice(0, 7) > lastEntry.slice(0, 7),
        };
    }, [lines, base]);

    /**
     * Divide a diferença para o valor da venda igualmente entre as parcelas em aberto (entradas só se
     * não houver parcela editável). Em centavos: o resto vai para as últimas, e o total fecha exato.
     */
    const redistribute = () => {
        const editable = (kind: Kind) => group(kind).filter((line) => !line.locked && !line.dropped);
        const targets = editable('installment').length > 0 ? editable('installment') : editable('entry');
        if (targets.length === 0 || live.diff === 0) return;
        const totalCents = Math.round(-live.diff * 100);
        const share = Math.trunc(totalCents / targets.length);
        const rest = totalCents - share * targets.length;
        const extra = new Map(
            targets.map((line, index) => {
                const bonus = index >= targets.length - Math.abs(rest) ? Math.sign(rest) : 0;
                return [line.uid, share + bonus];
            }),
        );
        setLines((prev) =>
            prev.map((line) =>
                extra.has(line.uid)
                    ? { ...line, amount: Math.round(((line.amount ?? 0) * 100 + (extra.get(line.uid) as number))) / 100 }
                    : line,
            ),
        );
        touch();
    };

    const hasErrors = (preview?.errors.length ?? 0) > 0;
    const canApply = !!preview && !stale && !hasErrors && live.ok && live.orderOk && reason.trim().length >= 3 && !simulate.isPending;

    const renderSection = (kind: Kind) => {
        const ordered = group(kind);
        let position = 0;

        return (
            <tbody className="divide-y divide-gray-100 dark:divide-zinc-800">
                <tr className="bg-gray-50/80 dark:bg-zinc-800/40">
                    <td colSpan={7} className="px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider text-gray-500 dark:text-zinc-400">
                        {t(kind === 'entry' ? 'v2.schedule_repair.section_entries' : 'v2.schedule_repair.section_installments')}
                    </td>
                </tr>
                {ordered.length === 0 && (
                    <tr>
                        <td colSpan={7} className="px-3 py-2 text-xs text-gray-400">{t('v2.schedule_repair.empty_section')}</td>
                    </tr>
                )}
                {ordered.map((line) => {
                    if (!line.dropped) position++;
                    const result = resultOf(line);
                    const isNew = line.id == null;
                    const changed = !isNew && !line.dropped && result?.action === 'change';
                    const movable = !line.locked && !line.dropped;
                    const label = t(kind === 'entry' ? 'v2.schedule_repair.entry_label' : 'v2.schedule_repair.installment_label', { n: position });

                    return (
                        <tr
                            key={line.uid}
                            draggable={movable}
                            onDragStart={() => setDragUid(line.uid)}
                            onDragEnd={() => setDragUid(null)}
                            onDragOver={(e) => {
                                if (dragUid && movable) e.preventDefault();
                            }}
                            onDrop={(e) => {
                                e.preventDefault();
                                if (dragUid) reorder(kind, dragUid, line.uid);
                                setDragUid(null);
                            }}
                            className={cn(
                                line.dropped && 'bg-rose-50/60 dark:bg-rose-950/20',
                                isNew && 'bg-emerald-50/50 dark:bg-emerald-950/10',
                                changed && 'bg-violet-50/40 dark:bg-violet-950/10',
                                dragUid === line.uid && 'opacity-40',
                            )}
                        >
                            <td className="w-8 pl-2">
                                {movable ? (
                                    <span className="flex cursor-grab items-center justify-center text-gray-400 hover:text-violet-600 active:cursor-grabbing" title={t('v2.schedule_repair.drag_hint')}>
                                        <PiDotsSixVerticalBold className="h-4 w-4" />
                                    </span>
                                ) : line.locked ? (
                                    <PiLockSimpleBold className="mx-auto h-3.5 w-3.5 text-gray-300" />
                                ) : null}
                            </td>
                            <td className="px-3 py-2">
                                <span className={cn('font-medium', line.dropped && 'text-rose-600 line-through')}>{line.dropped ? '—' : label}</span>
                                {line.locked && <span className="ml-2 text-[11px] text-gray-400">{t('v2.schedule_repair.paid_locked')}</span>}
                                {line.dropped && <span className="ml-2 text-[11px] font-semibold text-rose-600">{t('v2.schedule_repair.badge_drop')}</span>}
                                {isNew && <span className="ml-2 text-[11px] font-semibold text-emerald-600">{t('v2.schedule_repair.badge_add')}</span>}
                                {changed && <span className="ml-2 text-[11px] font-semibold text-violet-600">{t('v2.schedule_repair.badge_change')}</span>}
                            </td>
                            <td className="px-3 py-2">
                                <input
                                    type="date"
                                    value={line.due_date}
                                    disabled={!movable}
                                    onChange={(e) => update(line.uid, { due_date: e.target.value })}
                                    className={inputClass}
                                />
                            </td>
                            <td className="px-3 py-2">
                                <MoneyInput
                                    value={line.amount}
                                    disabled={!movable}
                                    onChange={(amount) => update(line.uid, { amount })}
                                    placeholder="R$ 0,00"
                                    className={cn(inputClass, 'text-right')}
                                />
                            </td>
                            <td className="px-3 py-2 text-right tabular-nums text-gray-500">{line.commission != null ? formatBrl(line.commission) : '—'}</td>
                            <td className="px-3 py-2 text-right font-semibold tabular-nums">
                                {line.dropped ? <span className="text-rose-600">{formatBrl(0)}</span> : result?.new_commission != null ? formatBrl(result.new_commission) : '—'}
                            </td>
                            <td className="px-3 py-2 text-right">
                                {!line.locked && (
                                    <button
                                        type="button"
                                        title={line.dropped ? t('v2.schedule_repair.restore') : t('v2.schedule_repair.remove')}
                                        onClick={() => {
                                            if (isNew) {
                                                setLines((prev) => prev.filter((l) => l.uid !== line.uid));
                                                touch();
                                            } else {
                                                update(line.uid, { dropped: !line.dropped });
                                            }
                                        }}
                                        className={cn(
                                            'rounded-lg p-1.5 transition',
                                            line.dropped ? 'text-gray-500 hover:bg-gray-100' : 'text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-950/40',
                                        )}
                                    >
                                        {line.dropped ? <PiArrowCounterClockwiseBold className="h-4 w-4" /> : <PiTrashBold className="h-4 w-4" />}
                                    </button>
                                )}
                            </td>
                        </tr>
                    );
                })}
                <tr>
                    <td colSpan={7} className="px-3 py-2">
                        <button
                            type="button"
                            onClick={() => {
                                setLines((prev) => [
                                    ...prev,
                                    { uid: `new-${Date.now()}`, id: null, kind, due_date: '', amount: null, locked: false, dropped: false, commission: null, original: null },
                                ]);
                                touch();
                            }}
                            className="inline-flex items-center gap-1.5 rounded-lg border border-dashed border-gray-300 px-3 py-1 text-xs font-medium text-gray-600 transition hover:border-violet-400 hover:text-violet-700 dark:border-zinc-700 dark:text-zinc-300"
                        >
                            <PiPlusBold className="h-3.5 w-3.5" />
                            {t(kind === 'entry' ? 'v2.schedule_repair.add_entry' : 'v2.schedule_repair.add')}
                        </button>
                    </td>
                </tr>
            </tbody>
        );
    };

    return (
        <V2Modal
            open={open}
            onClose={onClose}
            size="5xl"
            title={t('v2.schedule_repair.title')}
            description={t('v2.schedule_repair.subtitle')}
            footer={
                <div className="flex w-full flex-wrap items-center justify-between gap-2">
                    <p className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-zinc-400">
                        <PiLockSimpleBold className="h-3.5 w-3.5 shrink-0" />
                        {t('v2.schedule_repair.locked_note')}
                    </p>
                    <div className="flex gap-2">
                        <Button type="button" variant="outline" onClick={onClose}>{t('v2.schedule_repair.cancel')}</Button>
                        <Button type="button" variant="outline" isLoading={simulate.isPending} onClick={() => simulate.mutate(payload)}>
                            {t('v2.schedule_repair.simulate')}
                        </Button>
                        <Button type="button" color="primary" isLoading={apply.isPending} disabled={!canApply} onClick={() => apply.mutate()}>
                            {t('v2.schedule_repair.apply')}
                        </Button>
                    </div>
                </div>
            }
        >
            {!base ? (
                <div className="space-y-2">
                    {Array.from({ length: 6 }, (_, i) => <Skeleton key={i} className="h-9 w-full" />)}
                </div>
            ) : (
                <div className="space-y-4">
                    <div className="grid gap-3 sm:grid-cols-4">
                        <Stat label={t('v2.schedule_repair.deal_amount')} value={formatBrl(base.deal.amount)} />
                        <Stat label={t('v2.schedule_repair.total_after')} value={`${live.count} · ${formatBrl(live.sum)}`} />
                        <Stat
                            label={t('v2.schedule_repair.difference')}
                            value={live.ok ? `${t('v2.schedule_repair.difference_ok')}${live.diff !== 0 ? ` (${formatBrl(live.diff)})` : ''}` : formatBrl(live.diff)}
                            tone={live.ok ? 'success' : 'danger'}
                            action={
                                live.diff !== 0 ? (
                                    <button
                                        type="button"
                                        onClick={redistribute}
                                        title={t('v2.schedule_repair.redistribute_hint', { amount: formatBrl(-live.diff) })}
                                        className="mt-1.5 inline-flex items-center gap-1 rounded-md border border-current/20 bg-white/70 px-2 py-0.5 text-[11px] font-semibold text-violet-700 transition hover:bg-white dark:bg-zinc-900/60 dark:text-violet-300"
                                    >
                                        <PiEqualsBold className="h-3 w-3" />
                                        {t('v2.schedule_repair.redistribute')}
                                    </button>
                                ) : null
                            }
                        />
                        <label className="block">
                            <span className="text-xs font-medium text-gray-500 dark:text-zinc-400">{t('v2.schedule_repair.rate')}</span>
                            <input
                                value={rate}
                                onChange={(e) => {
                                    setRate(e.target.value);
                                    touch();
                                }}
                                inputMode="decimal"
                                placeholder={preview?.sellers[0] ? preview.sellers[0].rate_pp.toFixed(2).replace('.', ',') : ''}
                                className={cn(inputClass, 'mt-1')}
                            />
                            <span className="mt-0.5 block text-[11px] text-gray-400">{t('v2.schedule_repair.rate_hint')}</span>
                        </label>
                    </div>

                    {mirror && (
                        <div className="rounded-xl border border-sky-200 bg-sky-50/70 p-3 text-sm dark:border-sky-900/50 dark:bg-sky-950/20">
                            <div className="flex flex-wrap items-start justify-between gap-3">
                                <div className="space-y-0.5">
                                    <p className="font-semibold text-gray-900 dark:text-zinc-100">
                                        {t('v2.schedule_repair.ploomes_title')}
                                        {(mirror.quote_number ?? mirror.quote_id) ? (
                                            <span className="ml-2 text-xs font-normal text-gray-500">
                                                {t('v2.schedule_repair.ploomes_quote', { id: String(mirror.quote_number ?? mirror.quote_id) })}
                                            </span>
                                        ) : null}
                                    </p>
                                    <p
                                        className={cn(
                                            'text-xs font-semibold',
                                            mirror.origin_matches === true && 'text-emerald-700 dark:text-emerald-400',
                                            mirror.origin_matches === false && 'text-rose-600 dark:text-rose-400',
                                            mirror.origin_matches === null && 'text-gray-500 dark:text-zinc-400',
                                        )}
                                    >
                                        {mirror.origin_matches === true
                                            ? t('v2.schedule_repair.ploomes_origin_ok', { order: base.deal.sale_number })
                                            : mirror.origin_matches === false
                                              ? t('v2.schedule_repair.ploomes_origin_diff', { order: base.deal.sale_number })
                                              : t('v2.schedule_repair.ploomes_origin_none', { order: base.deal.sale_number })}
                                    </p>
                                    <p className="text-gray-700 dark:text-zinc-300">
                                        {t('v2.schedule_repair.ploomes_entries', { count: mirror.entries.length, total: formatBrl(mirror.entries.reduce((a, r) => a + r.amount, 0)) })}
                                        {' · '}
                                        {t('v2.schedule_repair.ploomes_installments', { count: mirror.installments.length, total: formatBrl(mirror.installments.reduce((a, r) => a + r.amount, 0)) })}
                                    </p>
                                    <p className="text-gray-700 dark:text-zinc-300">
                                        {t('v2.schedule_repair.ploomes_total')}: <span className="font-semibold">{formatBrl(mirror.sum)}</span>
                                    </p>
                                    <p className="text-xs text-gray-500 dark:text-zinc-400">
                                        {t('v2.schedule_repair.ploomes_hub', { count: live.count, total: formatBrl(live.sum) })}
                                    </p>
                                    {Math.abs(mirror.sum - mirror.deal_amount) > 0.01 && (
                                        <p className="text-xs font-semibold text-rose-600 dark:text-rose-400">
                                            {t('v2.schedule_repair.ploomes_vs_deal', { diff: formatBrl(mirror.sum - mirror.deal_amount) })}
                                        </p>
                                    )}
                                </div>
                                <Button type="button" size="sm" color="primary" onClick={applyMirror}>
                                    {t('v2.schedule_repair.ploomes_apply')}
                                </Button>
                            </div>
                        </div>
                    )}

                    <div className="flex flex-wrap items-center justify-between gap-2">
                        <p className="text-xs text-gray-500 dark:text-zinc-400">{t('v2.schedule_repair.drag_hint')}</p>
                        <Button type="button" size="sm" variant="outline" isLoading={loadMirror.isPending} onClick={() => loadMirror.mutate()}>
                            <PiCloudArrowDownBold className="mr-1.5 h-4 w-4" />
                            {t('v2.schedule_repair.ploomes_button')}
                        </Button>
                    </div>

                    <div className="overflow-x-auto rounded-xl border border-gray-200 dark:border-zinc-800">
                        <table className="w-full min-w-[800px] text-sm">
                            <thead className="text-left text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-zinc-400">
                                <tr>
                                    <th className="w-8" />
                                    <th className="px-3 py-2">{t('v2.schedule_repair.col_installment')}</th>
                                    <th className="px-3 py-2">{t('v2.schedule_repair.col_due')}</th>
                                    <th className="px-3 py-2">{t('v2.schedule_repair.col_amount')}</th>
                                    <th className="px-3 py-2 text-right">{t('v2.schedule_repair.col_commission')}</th>
                                    <th className="px-3 py-2 text-right">{t('v2.schedule_repair.col_new_commission')}</th>
                                    <th className="w-10 px-3 py-2" />
                                </tr>
                            </thead>
                            {renderSection('entry')}
                            {renderSection('installment')}
                        </table>
                    </div>

                    {!live.orderOk && (
                        <p className="flex items-start gap-2 rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-700 dark:bg-rose-950/30 dark:text-rose-300">
                            <PiWarningBold className="mt-0.5 h-4 w-4 shrink-0" />
                            {t('v2.schedule_repair.errors.entry_after_installment', {
                                entry: (lines.filter((l) => l.kind === 'entry' && !l.dropped && validDate(l.due_date)).map((l) => l.due_date).sort().pop() ?? '').split('-').reverse().join('/'),
                                installment: (lines.filter((l) => l.kind === 'installment' && !l.dropped && validDate(l.due_date)).map((l) => l.due_date).sort()[0] ?? '').split('-').reverse().join('/'),
                            })}
                        </p>
                    )}

                    {preview && (
                        <div className="space-y-2">
                            {stale && (
                                <p className="rounded-lg bg-amber-50 px-3 py-2 text-xs font-medium text-amber-800 dark:bg-amber-950/30 dark:text-amber-200">
                                    {t('v2.schedule_repair.preview_stale')}
                                </p>
                            )}
                            {preview.sellers.map((seller) => (
                                <p key={seller.user_id} className="rounded-lg bg-gray-50 px-3 py-2 text-sm text-gray-700 dark:bg-zinc-800/60 dark:text-zinc-200">
                                    {t('v2.schedule_repair.seller_total', {
                                        name: seller.name,
                                        target: formatBrl(seller.target),
                                        rate: seller.rate_pp.toFixed(2).replace('.', ','),
                                        current: formatBrl(seller.current),
                                        paid: formatBrl(seller.paid),
                                    })}
                                </p>
                            ))}
                            {preview.reattached.map((item) => (
                                <p key={item.record_id} className="text-xs text-gray-500 dark:text-zinc-400">
                                    {t('v2.schedule_repair.reattached', { amount: formatBrl(item.amount), label: item.label })}
                                </p>
                            ))}
                            {!stale &&
                                [...preview.warnings, ...preview.errors].map((message) => (
                                    <p
                                        key={message}
                                        className={cn(
                                            'flex items-start gap-2 rounded-lg px-3 py-2 text-sm',
                                            preview.errors.includes(message)
                                                ? 'bg-rose-50 text-rose-700 dark:bg-rose-950/30 dark:text-rose-300'
                                                : 'bg-amber-50 text-amber-800 dark:bg-amber-950/30 dark:text-amber-200',
                                        )}
                                    >
                                        <PiWarningBold className="mt-0.5 h-4 w-4 shrink-0" />
                                        {message}
                                    </p>
                                ))}
                        </div>
                    )}

                    <label className="block">
                        <span className="text-sm font-medium text-gray-700 dark:text-zinc-200">{t('v2.schedule_repair.reason')}</span>
                        <textarea
                            value={reason}
                            onChange={(e) => setReason(e.target.value)}
                            rows={2}
                            placeholder={t('v2.schedule_repair.reason_ph')}
                            className={cn(inputClass, 'mt-1')}
                        />
                    </label>
                </div>
            )}
        </V2Modal>
    );
}

const statTone = {
    default: 'border-gray-200 dark:border-zinc-800',
    success: 'border-emerald-200 bg-emerald-50 dark:border-emerald-900/50 dark:bg-emerald-950/20',
    danger: 'border-rose-200 bg-rose-50 dark:border-rose-900/50 dark:bg-rose-950/20',
};

function Stat({ label, value, tone = 'default', action }: { label: string; value: string; tone?: keyof typeof statTone; action?: React.ReactNode }) {
    return (
        <div className={cn('rounded-xl border px-3 py-2', statTone[tone])}>
            <p className="text-xs font-medium text-gray-500 dark:text-zinc-400">{label}</p>
            <p className="mt-0.5 font-semibold tabular-nums text-gray-900 dark:text-zinc-100">{value}</p>
            {action}
        </div>
    );
}
